import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import { USER_KIND_USER } from "~/constants/userKinds";
import {
  AiModel,
  AiThinkingLevel,
  AiVoice,
  EmojiSkinTone,
  EventActor,
  FolderSort,
  TaskDetailMode,
  Theme,
  UserRole,
  UserStatus,
  ViewMode,
} from "~/shared/enums";
import type { Recents, User } from "~/shared/types";

export const DEFAULT_RECENTS: Recents = {
  recentTaskStack: [],
  recentDocStack: [],
  recentSearchStack: [],
  recentIconStack: [],
  recentEmojiStack: [],
  recentColorCombinedStack: [],
  skinTone: EmojiSkinTone.NONE,
  createTaskCreateMore: false,
  lastBannerClearedAt: null,
  lastCodingLanguage: null,
};

const STANDARD_PSEUDO_USER_PARTIAL = {
  status: UserStatus.ACTIVE,
  role: UserRole.MEMBER,
  theme: Theme.SYSTEM_DEFAULT,
  imageUrl: null,
  timezone: null,
  taskDetailMode: TaskDetailMode.RIGHTBAR,
  viewMode: ViewMode.PROJECTS,
  calendarDuid: null,
  aiLanguage: null,
  aiModel: AiModel.AUTO,
  aiThinkingLevel: AiThinkingLevel.LOW,
  aiGuidelines: null,
  aiVoice: AiVoice.MARIN,
  aiVoiceAutoplay: false,
  manualDescription: null,
  generatedDescription: null,
  usingGeneratedDescription: true,
  sections: { expanded: [], collapsed: [] },
  layout: {
    rightbarWidthPx: 650,
    leftbarWidthPx: 225,
    fullscreenRightbarWidthPx: 600,
    chatPanelWidthPx: 400,
    docSort: FolderSort.ORDER,
  },
  recents: DEFAULT_RECENTS,
  tutorialStatuses: {},
  authToken: null,
  googleData: null,
  isAdmin: false,
  agent: null,
};

export const FORM_USER_PSEUDO_USER_KEY = "@FormUser";
export const FORM_USER_DISPLAY_VALUE = EventActor.FORM_USER;
export const FORM_USER_PSEUDO_USER: User = {
  duid: FORM_USER_PSEUDO_USER_KEY,
  email: FORM_USER_PSEUDO_USER_KEY,
  kind: USER_KIND_USER,
  name: FORM_USER_DISPLAY_VALUE,
  colorHex: DEFAULT_CHIP_COLOR,
  ...STANDARD_PSEUDO_USER_PARTIAL,
};

export const DEFAULT_USER: User = {
  duid: "",
  email: "",
  kind: USER_KIND_USER,
  name: "",
  colorHex: DEFAULT_CHIP_COLOR,
  ...STANDARD_PSEUDO_USER_PARTIAL,
};

export const ORDERED_ROLES = [
  UserRole.FINANCIAL_ADMIN,
  UserRole.TECHNICAL_ADMIN,
  UserRole.GUEST,
  UserRole.MEMBER,
  UserRole.ADMIN,
];
if (ORDERED_ROLES.length !== Object.values(UserRole).length) {
  throw new Error("ORDERED_ROLES length does not match UserRole length");
}

export const USER_ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.ADMIN]:
    "Full workspace control, including teammate roles, workspace settings, properties, billing, and integrations",
  [UserRole.MEMBER]:
    "Can work in shared team spaces, use forms and imports, and manage personal integrations without admin controls",
  [UserRole.GUEST]:
    "Limited to Personal space and explicitly shared spaces, without General space, workspace settings, or AI features",
  [UserRole.TECHNICAL_ADMIN]:
    "For IT admins who manage integrations, SSO, and SCIM without regular workspace access or assignee visibility",
  [UserRole.FINANCIAL_ADMIN]:
    "For finance teammates who manage plans, billing, and invoices without regular workspace access or assignee visibility",
};
